import React from 'react';
import { gql, useQuery } from '@apollo/client';

import { PLAYER_MILESTONE_FIELDS } from '../../../lib/queries/player.queries';
import { useRecordPlayerEdit } from '../../../lib/services/player-session.service';
import { buildMilestoneAction } from '../../../lib/helpers/player-action.helper';
import { PlayerMilestoneSection } from './player-milestone-section.component';

const GET_PLAYER_MILESTONES = gql`
  query GetPlayerMilestones($playerAssessmentId: ID!) {
    playerAssessment(_id: $playerAssessmentId) {
      _id
      player {
        _id
        milestones {
          ...PlayerMilestoneFields
        }
      }
    }
  }

  ${PLAYER_MILESTONE_FIELDS}
`;

interface IPlayerMilestonesGraphQLProps {
  playerId: string;
  playerAssessmentId: string;
}

export const PlayerMilestonesGraphQL: React.FC<IPlayerMilestonesGraphQLProps> = ({ playerId, playerAssessmentId }) => {
  const { data } = useQuery(GET_PLAYER_MILESTONES, {
    variables: { playerAssessmentId },
    fetchPolicy: 'cache-and-network',
  });

  const recordPlayerEdit = useRecordPlayerEdit(['GetPlayerMilestones']);

  const milestones: IPlayerMilestone[] | undefined = data?.playerAssessment?.player?.milestones;

  const onRecord = ({ type, value }: { type: PlayerMilestoneType; value: number }) => {
    const recordedAt = new Date().toISOString();
    const action = buildMilestoneAction(playerId, playerAssessmentId, { type, value, recordedAt });

    return recordPlayerEdit(action, {
      // Not authoritative: the temp _id is a placeholder the GetPlayerMilestones
      // refetch replaces with the server-assigned record.
      optimisticUpdate: (cache) => {
        const tempId = `temp:${recordedAt}:${type}`;
        const milestone = { __typename: 'PlayerMilestone' as const, _id: tempId, type, value, recordedAt };
        const playerCacheId = cache.identify({ __typename: 'Player', _id: playerId });

        cache.writeFragment({ id: cache.identify(milestone), fragment: PLAYER_MILESTONE_FIELDS, data: milestone });
        cache.modify({
          id: playerCacheId,
          fields: { milestones: (existing = [], { toReference }) => [...existing, toReference(milestone)] },
        });

        return () => cache.modify({
          id: playerCacheId,
          fields: { milestones: (existing = [], { readField }) => existing.filter((ref: any) => readField('_id', ref) !== tempId) },
        });
      },
    });
  };

  return <PlayerMilestoneSection milestones={milestones} onRecord={onRecord} />;
};
